import axios from 'axios'
import toast from 'react-hot-toast'
import { auth } from './firebase'
import logger from '../utils/logger'

// Base API configuration - automatically detect localhost vs production
const getApiBaseUrl = () => {
    // Check if environment variable is set (takes priority)
    if (import.meta.env.VITE_API_BASE_URL) {
        return import.meta.env.VITE_API_BASE_URL
    }

    // Auto-detect based on hostname
    const isLocalhost = window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === '0.0.0.0'

    if (isLocalhost) {
        return 'http://localhost:5001'
    }

    // Production URL
    return 'https://aums-server-619565673640.asia-south1.run.app'
}

const API_BASE_URL = getApiBaseUrl()

// Create axios instance
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // 30 seconds
    headers: {
        'Content-Type': 'application/json',
    },
})

// Request interceptor
apiClient.interceptors.request.use(
    async (config) => {
        // Add Firebase user UID header if user is authenticated
        if (auth?.currentUser) {
            config.headers['x-user-uid'] = auth.currentUser.uid
        }

        // Add dealership ID header if available
        try {
            const storedDealership = localStorage.getItem('currentDealership')
            if (storedDealership) {
                const dealership = JSON.parse(storedDealership)
                if (dealership?.dealershipId) {
                    config.headers['x-dealership-id'] = dealership.dealershipId
                }
            }
        } catch (error) {
            // Silently fail if dealership not found or parsing fails
            logger.warn('Failed to get dealership ID from localStorage:', error)
        }

        // Add auth token if available
        const token = localStorage.getItem('auth-token')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }

        // Log request in development
        logger.logApiRequest(config)

        return config
    },
    (error) => {
        logger.error('Request interceptor error:', error)
        return Promise.reject(error)
    }
)

/**
 * Extract user-friendly error message from server response
 * @param {Object} error - Axios error object
 * @returns {string} User-friendly error message
 */
const extractErrorMessage = (error) => {
    const { response } = error

    // If no response, it's a network error
    if (!response) {
        if (error.code === 'ECONNABORTED') {
            return 'Request timeout. Please check your connection and try again.'
        }
        if (error.message?.includes('Network Error')) {
            return 'Network error. Please check your internet connection and try again.'
        }
        return 'Unable to connect to server. Please check your connection and try again.'
    }

    const { data, status } = response

    // Server returns standardized error format: { success: false, message: "...", errors: [...], code: "..." }
    if (data) {
        // Check for validation errors array (field-specific errors)
        if (Array.isArray(data.errors) && data.errors.length > 0) {
            // Format validation errors into user-friendly message
            const errorMessages = data.errors
                .map(err => {
                    // If error has field and message, format it nicely
                    if (err.field && err.message) {
                        const fieldName = err.field
                            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                            .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
                            .trim()
                        return `${fieldName}: ${err.message}`
                    }
                    // If it's just a message string
                    return typeof err === 'string' ? err : err.message || 'Invalid value'
                })
                .filter(Boolean) // Remove any empty strings

            if (errorMessages.length > 0) {
                // If multiple errors, show first one in toast, but include count
                if (errorMessages.length === 1) {
                    return errorMessages[0]
                } else {
                    // Show first error with count
                    return `${errorMessages[0]} (and ${errorMessages.length - 1} more error${errorMessages.length - 1 > 1 ? 's' : ''})`
                }
            }
        }

        // Use server-provided message if available
        if (data.message) {
            return data.message
        }

        // Fallback to error property
        if (data.error) {
            return typeof data.error === 'string' ? data.error : 'An error occurred'
        }
    }

    // HTTP status code-based fallback messages
    switch (status) {
        case 400:
            return 'Invalid request. Please check your input and try again.'
        case 401:
            return 'Session expired. Please login again.'
        case 403:
            return 'Access denied. You don\'t have permission to perform this action.'
        case 404:
            return 'The requested resource was not found.'
        case 409:
            return 'This action conflicts with existing data. Please check and try again.'
        case 422:
            return 'Validation error. Please check your input and try again.'
        case 429:
            return 'Too many requests. Please wait a moment and try again.'
        case 500:
        case 502:
        case 503:
        case 504:
            return 'Server error. Please try again later. If the problem persists, contact support.'
        default:
            return 'An unexpected error occurred. Please try again.'
    }
}

// Response interceptor
apiClient.interceptors.response.use(
    (response) => {
        // Log response in development
        logger.logApiResponse(response)

        return response
    },
    (error) => {
        // Handle common errors
        const { response } = error

        logger.logApiError(error)

        // Extract user-friendly error message
        const errorMessage = extractErrorMessage(error)

        // Handle specific error cases that need special treatment
        if (response?.status === 401) {
            // Unauthorized - clear auth data
            localStorage.removeItem('auth-token')
            window.location.href = '/login'
            toast.error(errorMessage)
        } else if (response?.status === 403) {
            // Check if this is a dealer-related endpoint that might fail silently
            const url = error.config?.url || ''
            const isDealerEndpoint = url.includes('/dealers') || (url.includes('/vehicles/') && url.includes('/dealers'))
            const isVehicleInventoryPage = window.location.pathname.includes('/vehicle-inventory')

            // Suppress error for dealer endpoints when NOT on vehicle inventory page
            // Show error for all other 403 errors or when on vehicle inventory page
            if (!isDealerEndpoint || isVehicleInventoryPage) {
                toast.error(errorMessage)
            }
            // Otherwise, fail silently (for dealer endpoints when not on vehicle inventory page)
        } else {
            // Show user-friendly error message for all other errors
            toast.error(errorMessage)
        }

        // Attach error message and validation errors to error object for component-level handling
        if (response?.data) {
            error.userMessage = errorMessage
            error.validationErrors = Array.isArray(response.data.errors) ? response.data.errors : null
            error.errorCode = response.data.code || null
        } else {
            error.userMessage = errorMessage
        }

        return Promise.reject(error)
    }
)

// Generic API methods
export const api = {
    // GET request
    get: async (url, params = {}) => {
        try {
            const response = await apiClient.get(url, { params })
            return response.data
        } catch (error) {
            throw error
        }
    },

    // POST request
    post: async (url, data = {}) => {
        try {
            const response = await apiClient.post(url, data)
            return response.data
        } catch (error) {
            throw error
        }
    },

    // PUT request
    put: async (url, data = {}) => {
        try {
            const response = await apiClient.put(url, data)
            return response.data
        } catch (error) {
            throw error
        }
    },

    // PATCH request
    patch: async (url, data = {}) => {
        try {
            const response = await apiClient.patch(url, data)
            return response.data
        } catch (error) {
            throw error
        }
    },

    // DELETE request
    delete: async (url) => {
        try {
            const response = await apiClient.delete(url)
            return response.data
        } catch (error) {
            throw error
        }
    },

    // Upload file
    upload: async (url, formData, onProgress = null) => {
        try {
            const response = await apiClient.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: onProgress,
            })
            return response.data
        } catch (error) {
            throw error
        }
    },
}

// Utility functions
export const setAuthToken = (token) => {
    if (token) {
        localStorage.setItem('auth-token', token)
        apiClient.defaults.headers.Authorization = `Bearer ${token}`
    } else {
        localStorage.removeItem('auth-token')
        delete apiClient.defaults.headers.Authorization
    }
}

export const clearAuthToken = () => {
    localStorage.removeItem('auth-token')
    delete apiClient.defaults.headers.Authorization
}

export default apiClient
